import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { Badge } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReportingClient, type GlobalFX } from "../api/reporting";
import { MonthlyPanels } from "../sales/MonthlyPanels";
import { SalesGrid } from "../sales/SalesGrid";
import { useSalesRows, useSalesWindows, type GridRow } from "../sales/windows";
import { NexpimView } from "../views";
import { ClientDrillDown } from "./sales/ClientDrillDown";
import { ANALYSIS_PRESETS } from "./sales/periods";

/**
 * Sales → Analysis: every client against the periods on the strip.
 *
 * The screen used to be three hard-coded columns — month, quarter, year to
 * date — with a button to add more. The columns are now whichever chips on
 * the period strip are lit, and every one of them, preset or custom, carries
 * its own comparison under the same two toggles. The three defaults are still
 * lit when the page opens.
 *
 * The two cards above the grid are not its totals — those are its first two
 * rows now, pinned, so they follow the grid to any width. The cards are the
 * year so far, month over month, per currency: money and units on their own
 * axes, on the same basis and scope as the grid.
 */
export function SalesAnalysisPage() {
  const { t } = useTranslation();
  const { http, hasView } = useAuth();
  const client = useMemo(() => new ReportingClient(http), [http]);
  const allowed = hasView(NexpimView.SALES_ANALYSIS);

  const windows = useSalesWindows({ mode: "multi", defaults: ANALYSIS_PRESETS });
  const [drill, setDrill] = useState<GridRow | null>(null);

  const fxQuery = useQuery({
    queryKey: ["globalfx"],
    queryFn: () => client.globalFx() as Promise<GlobalFX>,
    staleTime: 60 * 60_000,
    enabled: allowed,
  });

  const { rows, isLoading, error } = useSalesRows(client, windows.columns, fxQuery.data, allowed);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.sales-analysis", "Analysis")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  const customCount = windows.selected.filter((entry) => entry.custom).length;

  return (
    <>
      <PageHeader
        title={t("navigation.item.sales-analysis", "Analysis")}
        subtitle={t("analysis.subtitle", "Invoiced sales per client.")}
        badges={
          customCount > 0 ? (
            <Badge variant="accent" size="sm">
              {t("analysis.custom-count", "{{count}} custom", { count: customCount })}
            </Badge>
          ) : null
        }
      />

      {error ? (
        <div className="p-4 lg:p-6">
          <p className="rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
            {t("error.generic", "Something went wrong. Please try again.")}
          </p>
        </div>
      ) : (
        <>
          <MonthlyPanels client={client} />

          <SalesGrid
            rows={rows}
            columns={windows.columns}
            loading={isLoading}
            stateKey="/sales/analysis"
            selected={windows.selected}
            onToggle={windows.toggle}
            onRemove={windows.remove}
            comparison={windows.comparison}
            onComparison={windows.setComparison}
            onDrill={setDrill}
          />
        </>
      )}

      {drill && <ClientDrillDown client={drill} columns={windows.columns} onClose={() => setDrill(null)} />}
    </>
  );
}
